import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";

import GoogleMapView from "../components/GoogleMapView";
import DashboardMap from "../components/DashboardMap";

const MapPage = () => {
  // ===============================
  // STATES
  // ===============================
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [path, setPath] = useState([]);
  
  // FIX: Changed default from "last" to "today" so changing dropdowns 
  // loads a collection of points immediately, instead of a single un-renderable point.
  const [filterType, setFilterType] = useState("today"); 
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ===============================
  // GET LOGGED USER + ROLE
  // ===============================
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("users")
        .select("*")
        .eq("auth_id", user.id)
        .single();

      if (data) {
        setCurrentUser(data);
        setSelectedUser(data.auth_id); // Default to viewing own data
      }
    };
    getUser();
  }, []);

  // ===============================
  // LOAD USERS (Super Admin Only)
  // ===============================
  useEffect(() => {
    if (currentUser?.role !== "super_admin") return;

    const fetchUsers = async () => {
      const { data } = await supabase
        .from("users")
        .select("*")
        .order("name", { ascending: true });
      if (data) setUsers(data);
    };
    fetchUsers();
  }, [currentUser]);

  // ===============================
  // FETCH & FILTER HISTORY
  // ===============================
  const fetchHistory = useCallback(async () => {
    if (!selectedUser) return;

    const { data, error } = await supabase.rpc("get_user_history", {
      p_user_id: selectedUser,
    });

    if (error || !data || data.length === 0) {
      setPath([]);
      return;
    }

    let filtered = [...data];

    if (filterType === "last") {
      filtered = [filtered[filtered.length - 1]];
    } else if (filterType === "today") {
      const today = new Date().toISOString().split("T")[0];
      filtered = filtered.filter((p) => p.created_at?.startsWith(today));
    } else if (filterType === "7days") {
      const last7 = new Date();
      last7.setDate(last7.getDate() - 7);
      filtered = filtered.filter((p) => new Date(p.created_at) >= last7);
    } else if (filterType === "custom") {
      filtered = filtered.filter((p) => {
        const d = new Date(p.created_at);
        return (
          (!fromDate || d >= new Date(fromDate)) &&
          (!toDate || d <= new Date(toDate + "T23:59:59"))
        );
      });
    }

    // FIX: Included 'created_at' in the map array transformation 
    // so the GoogleMapView child can parse the timestamp for the popups.
    const formatted = filtered
      .map((p) => ({
        lat: Number(p.lat),
        lng: Number(p.lng),
        created_at: p.created_at, 
      }))
      .filter((p) => !isNaN(p.lat) && !isNaN(p.lng));

    setPath(formatted);
  }, [selectedUser, filterType, fromDate, toDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleHide = () => setPath([]);

  return (
    <>
      <div
        style={{
          flex: 1, 
          display: "flex",
          flexDirection: "column",
          position: "relative",
          width: "100%",
          overflow: "hidden", 
        }}
      >
        <div style={{ flex: 1, width: "100%", position: "relative" }}>
           <GoogleMapView path={path} />
        </div>

        <DashboardMap
          user={currentUser}
          path={path}
          filterType={filterType}
          setFilterType={setFilterType}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          users={users}
          handleHide={handleHide}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
        />
      </div>
    </>
  );
};

export default MapPage;